import React, { ReactElement } from 'react';
import {
  Component,
  FilterCombo, HBox,
  Position,
  PuzzleButton, VBox
} from "puzzle";
import classNames from "classnames";
import { CUSTOM_ATTRIBUTE, PREVIEW_TEMPLATE_STATE, REFERENCE_FILE } from './Types';
import * as _ from 'lodash';
import {
  ComboBoxChangeEvent,
  ComboBoxFilterChangeEvent
} from "@progress/kendo-react-dropdowns";
import { Reveal } from '@progress/kendo-react-animation';
import { orderBy } from '@progress/kendo-data-query';
import {
  addDeletedTargetLanguages,
  createTargetLanguageTagClass,
  getMatchingDisplayName,
  translateCustomAttributes
} from "../../util/Util";
import { LANGUAGE_DIRECTIONS } from "../../Drawer/Types";

class PreviewTemplates extends Component<{}, PREVIEW_TEMPLATE_STATE> {
  searchDelayTimer: any;
  state = {
    data: [],
    files: [],
    selectedTemplate: null,
    showDetails: true,
    templateDetails: {
      batchInfos: [],
      creator: '',
      dateStarted: '',
      dueDate: '',
      languageDirections: [],
      owners: [],
      project: '',
      targetLanguages: [],
      sourceLanguage: '',
      submitterInfos: []
    },
    workflow: null
  };

  /**
   * Close button handler
   */
  closeDialog = () => {
    this.dispatch('closeDialog');
  };

  /**
   * Custom attributes render
   * @param customAttributes
   */
  customAttributesRender = (customAttributes: CUSTOM_ATTRIBUTE[]): JSX.Element => {
    const render = customAttributes.map((item: CUSTOM_ATTRIBUTE) => {
      let translatedName = translateCustomAttributes(item.name);
      return (
        <span key={'attributes' + translatedName} className={'tp-custom-attribute-tag'}>
          <span>{translatedName}: </span>
          <span>{item.value}</span>
      </span>)
    });

    return (
      <div className={'tp-templatePreview-details-row'}>
        <span className={'tp-templatePreview-details-label'}>
          {this.translate('customAttributes')}
        </span>
        <span className={'tp-templatePreview-details-right'}>
          {render}
        </span>
      </div>
    );
  };

  /**
   *  Open's new submission with selected template and dropped files
   */
  createFromTemplate = () => {
    const { files, selectedTemplate } = this.state;
    this.dispatch('createFromTemplate', Position.end, { files, selectedTemplate });
  };

  /**
   * Filter change handler
   * @param event
   */
  filterChange = (event: ComboBoxFilterChangeEvent): void => {
    const isInput = event.nativeEvent.type === 'input';
    clearTimeout(this.searchDelayTimer);
    (this.searchDelayTimer as any) = setTimeout(this.filterTemplates.bind(this, event.filter.value), isInput ? 300 : 0);
  };

  /**
   * Refreshes data within the dropdown
   * @param value
   */
  filterTemplates = (value: string): void => {
    this.dispatch('templatesSearch', {
      pos: Position.start,
      persistent: ['loadNewData']
    }, {
      loadNewData: true,
      submissionNameInputText: value
    });
  };

  /**
   * Gets details data for currently selected template
   * @param value
   */
  getDetails = (value?: string) => {
    const { showDetails, selectedTemplate } = this.state;
    if (!showDetails) {
      this.dispatch('getTemplateDetails', Position.end, { submissionId: value || selectedTemplate });
    }
  };

  /**
   * Returns ordered and filtered data
   *
   * @param items
   * @returns data
   */
  getFilteredTemplateData = (items: any): any[] => {
    const selectableTpls = items?.filter((item: any) => item.userContextPolicies?.POLICY_PD_ADD_SUBMISSION_FROM_TEMPLATE);
    const data = orderBy(selectableTpls, [{
      field: 'isFavorite',
      dir: 'desc'
    }, {
      field: 'submissionName',
      dir: 'asc'
    }]);
    return data;
  };

  /**
   * Template dropDown list item render
   * @param li
   * @param itemProps
   */
  itemRender = (li: ReactElement<HTMLLIElement>, itemProps: any): React.ReactNode => {
    const { isFavorite } = itemProps.dataItem;
    const iconClass = classNames(
      'tp-icon tp-icon-sm',
      {
        'tp-star-outline tp-gray-color': !isFavorite,
        'tp-star tp-favorite-color': isFavorite
      }
    );
    const props = {
      ...li.props,
      id: itemProps.id?.substr(7, itemProps.id?.length)
    };
    if (isFavorite) {
      Object.assign(props, { 'aria-label': `${this.translate('SRL-favoriteTemplateIcon')}` })
    }
    const itemChildren =
      <>
        <i className={iconClass} aria-hidden={true} />
        {li.props.children}
      </>;
    return React.cloneElement(li, props, itemChildren);
  };

  /**
   * Loads new templates on filter change
   *
   * @param data
   */
  loadFilteredTemplates = ({ items }: any) => {
    const data = this.getFilteredTemplateData(items);
    this.setState({ data });
  };

  /**
   * Handles template change action
   * @param event
   */
  onTemplateChange = (event: ComboBoxChangeEvent): void => {
    const { value } = event.target;
    if (value) {
      this.setState({
        selectedTemplate: value,
        showDetails: false
      }, this.getDetails.bind(this, value));
    } else {
      this.setState({
        selectedTemplate: value,
        showDetails: !!value
      })
    }
    this.dispatch('updateTemplatePreviewSaveButton', '', { buttonDisabled: !!value });
  };

  /**
   * Reference files render
   * @param referenceFiles
   */
  referenceFilesRender = (referenceFiles: REFERENCE_FILE[]): JSX.Element => {
    const render = referenceFiles.map((item: REFERENCE_FILE) => {
      return (<span key={'refFiles' + item.fileName} className={'tp-templatePreview-reference-files'}>
        {item.fileName}
      </span>)
    });

    return (
      <div className={'tp-templatePreview-details-row'}>
         <span className={'tp-templatePreview-details-label'}>
          {this.translate('referenceFiles')}
        </span>
        <span className={'tp-templatePreview-details-right'}>
          {render}
        </span>
      </div>
    );
  };

  /**
   * Sets template data items
   * @param data
   * @param files
   */
  setData = ({ items }: any, files = []): void => {
    const data = this.getFilteredTemplateData(items);
    this.setState({
      data,
      files: [...files]
    });

  };

  /**
   * Called by dispatcher gets data related to current selected template
   *
   * @param templateDetails
   * @param workflow
   */
  setTemplateDetails = (templateDetails: any, workflow: any): void => {
    const workflowName = _.find(workflow, (item: any) => templateDetails.batchInfos[0].workflowId === item.id)?.name;
    this.setState({
      templateDetails,
      workflow: workflowName || ''
    });
  };

  /**
   * Changes show details flag
   */
  showDetailsHandler = (): void => {
    const { selectedTemplate, showDetails } = this.state;
    if (selectedTemplate) {
      this.setState({
        showDetails: !showDetails
      });
    }
  };


  /**
   * Render of template details section
   */
  showTemplateDetails = (): JSX.Element => {
    const { templateDetails, workflow } = this.state;
    const { initializeUserData } = this.props;
    const description = _.get(templateDetails, 'description');
    const source = _.get(templateDetails, 'sourceLanguage');
    const targetLanguages = _.get(templateDetails, 'targetLanguages');
    const instructions = _.get(templateDetails, 'instructions');
    const referenceFiles = _.get(templateDetails, 'referenceFiles');
    const customAttributes = _.get(templateDetails, 'customAttributes');
    const sourceLanguageString = getMatchingDisplayName([source], 'sourceLanguageValues', initializeUserData);

    return (
      <div className={'tp-templatePreview-details-wrap'}>
        <div className='pd-tpl-details-title tp-font-size-md tp-text-semibold'>{this.translate('templateDetails')}</div>
        {description && (
          <div className={'tp-templatePreview-details-row'}>
            <span className='tp-templatePreview-details-label'>{this.translate('description')}</span>
            <span className={'tp-templatePreview-details-right'}>{description}</span>
          </div>
        )}
        {workflow && (
          <div className={'tp-templatePreview-details-row'}>
            <span className='tp-templatePreview-details-label'>
              {this.translate('workflow')}
            </span>
            <span className={'tp-templatePreview-details-right tp-templatePreview-details-workflow'}>{workflow}</span>
          </div>
        )}
        {source && (
          <div className={'tp-templatePreview-details-row'}>
            <span className='tp-templatePreview-details-label'>{this.translate('source')}</span>
            <span
              className={'tp-templatePreview-details-right tp-templatePreview-details-source'}>{sourceLanguageString}</span>
          </div>
        )}
        {
          //@ts-ignore
          (targetLanguages !== undefined && targetLanguages.length > 0) && this.targetLanguageRender(targetLanguages)
        }
        {instructions && (
          <div className={'tp-templatePreview-details-row'}>
            <span className='tp-templatePreview-details-label'>{this.translate('instructions')}</span>
            <span className={'tp-templatePreview-details-right tp-templatePreview-details-instructions'}
                  dangerouslySetInnerHTML={{ __html: instructions }} />
          </div>
        )}
        {
          //@ts-ignore
          (referenceFiles !== undefined && referenceFiles.length > 0) && this.referenceFilesRender(referenceFiles)
        }
        {
          //@ts-ignore
          (customAttributes !== undefined && customAttributes.length > 0) && this.customAttributesRender(customAttributes)
        }
      </div>
    );
  };

  /**
   * Target language render
   * @param targetLanguage
   */
  targetLanguageRender = (targetLanguage: string[]): JSX.Element => {
    const { templateDetails } = this.state;
    const { languageDirections, batchInfos } = templateDetails;
    const localizedTargetLanguages = addDeletedTargetLanguages(languageDirections, targetLanguage, batchInfos);
    const render = localizedTargetLanguages.map((item: LANGUAGE_DIRECTIONS) => {
      return (
        <span key={'lang' + item}
              className={`k-chip k-chip-md k-rounded-md k-chip-solid k-chip-solid-base ${createTargetLanguageTagClass(item)}`}>
          {item.targetLanguageLocale}
        </span>
      )
    });

    return (
      <div className={'tp-templatePreview-details-row'}>
        <span className={'tp-templatePreview-details-label'}>
          {this.translate('target')}
        </span>
        <span className={'tp-templatePreview-details-right'}>
            {render}
         </span>
      </div>
    );
  };

  renderBody() {
    const { showDetails, selectedTemplate, data, templateDetails } = this.state;
    let templateDetailsStyle = !selectedTemplate ? 'k-state-disabled' : '';
    let tplDetails = selectedTemplate && showDetails && templateDetails ? this.showTemplateDetails() : null;
    return (
      <VBox gap={4}>
        <HBox gap={2} alignItemsEnd>
          <FilterCombo
            data={data}
            filterChange={this.filterChange}
            itemRender={this.itemRender}
            label={this.translate('select')}
            name='template'
            onChange={this.onTemplateChange}
            popupSettings={{ className: `${this.props.zIndexCls} tp-templatePreview-select-dropdown` }}
            value={[selectedTemplate]}
            valueField='submissionId'
            textField='submissionName'
            translateMethod={this.translate.bind(this)}
            valueAsSimpleType={true}
          />
          <PuzzleButton
            fillMode={'flat'}
            themeColor={'primary'}
            tooltipClassName='tp-space-bottom-half-1'
            withTooltip
            titleText={!showDetails || !selectedTemplate ? this.translate('showDetails') : this.translate('hideDetails')}
            size={'small'}
            iconClass={'tp-icon tp-info'}
            onClick={this.showDetailsHandler}
            disabled={templateDetailsStyle}
            aria-label={!showDetails || !selectedTemplate ? this.translate('showDetails') : this.translate('hideDetails')}
          />
        </HBox>
        <Reveal className={'pd-animation-container'}>{tplDetails}</Reveal>
      </VBox>
    )
  }
}

export default PreviewTemplates;
